/**
* Message input component with file upload and send button
*/

import { useCallback, useEffect, useRef } from 'react'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import SendIcon from '@mui/icons-material/Send'
import type { ThemePalette } from '../utils/themeUtils'

interface MessageInputProps {
    inputValue: string
    onInputChange: (value: string) => void
    onSend: () => void
    onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
    fileInputRef: React.RefObject<HTMLInputElement | null>
    isSending: boolean
    isSendDisabled: boolean
    fileError?: string | null
    onClearFileError?: () => void
    palette: ThemePalette
    allowFileAttachment?: boolean // Allow file attachments (default: true)
    disabled?: boolean // Disable entire input area
    placeholder?: string // Custom placeholder text
}

export function MessageInput({
    inputValue,
    onInputChange,
    onSend,
    onFileSelect,
    fileInputRef,
    isSending,
    isSendDisabled,
    fileError,
    onClearFileError,
    palette,
    allowFileAttachment = true,
    disabled = false,
    placeholder = 'Type your message...',
}: MessageInputProps) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const adjustTextareaHeight = useCallback(() => {
        const textarea = textareaRef.current
        if (!textarea) return

        const MAX_ROWS = 6
        const LINE_HEIGHT = 20 // matches 14px font with 1.4 line-height
        const VERTICAL_PADDING = 24 // 12px top + 12px bottom
        const maxHeight = MAX_ROWS * LINE_HEIGHT + VERTICAL_PADDING

        textarea.style.height = 'auto'
        const newHeight = Math.min(textarea.scrollHeight, maxHeight)
        textarea.style.height = `${newHeight}px`
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
    }, [])

    useEffect(() => {
        adjustTextareaHeight()
    }, [inputValue, adjustTextareaHeight])

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault()
                onSend()
            }}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '12px 16px 16px',
                background: palette.panelBg,
                borderTop: `1px solid ${palette.border}`,
                position: 'relative',
            }}
        >
            {/* File validation error */}
            {fileError && (
                <div
                    role="alert"
                    style={{
                        color: '#dc2626',
                        fontSize: '12px',
                        lineHeight: 1.4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <span>{fileError}</span>
                    <button
                        type="button"
                        onClick={() => onClearFileError?.()}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontWeight: 700,
                            padding: '2px',
                            marginLeft: 'auto',
                            fontSize: '16px',
                            lineHeight: 1,
                        }}
                        aria-label="Dismiss file error"
                    >
                        ×
                    </button>
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Hidden file input - accepts all file types */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="*/*"
                    onChange={onFileSelect}
                    style={{ display: 'none' }}
                />

                {/* Text area with inlined attachment action */}
                <div
                    style={{
                        position: 'relative',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <textarea
                        className="widget-textarea"
                        ref={textareaRef}
                        value={inputValue}
                        disabled={disabled}
                        onChange={(event) => {
                            onInputChange(event.target.value)
                        }}
                        placeholder={placeholder}
                        rows={1}
                        style={{
                            width: '100%',
                            minHeight: '44px',
                            resize: 'none',
                            border: 'none',
                            borderRadius: '14px',
                            padding: allowFileAttachment ? '12px 52px 12px 14px' : '12px 14px',
                            fontSize: '14px',
                            lineHeight: 1.4,
                            fontFamily: 'inherit',
                            background: palette.inputBg,
                            color: palette.textPrimary,
                            boxShadow: 'inset 0 1px 4px rgba(15,23,42,0.08)',
                            boxSizing: 'border-box',
                            overflowY: 'hidden',
                            scrollbarWidth: 'thin',
                            opacity: disabled ? 0.6 : 1,
                            cursor: disabled ? 'not-allowed' : 'text',
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                if (!disabled) onSend()
                            }
                        }}
                    />

                    {allowFileAttachment && (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isSending}
                            aria-label="Attach file"
                            title="Attach file"
                            style={{
                                position: 'absolute',
                                right: '6px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                border: 'none',
                                background: 'transparent',
                                color: palette.textSecondary,
                                cursor: isSending ? 'not-allowed' : 'pointer',
                                opacity: isSending ? 0.5 : 1,
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                if (!isSending) {
                                    e.currentTarget.style.background = palette.border
                                    e.currentTarget.style.color = palette.textPrimary
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent'
                                e.currentTarget.style.color = palette.textSecondary
                            }}
                        >
                            <AttachFileIcon style={{ width: '22px', height: '22px' }} />
                        </button>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={isSendDisabled || isSending}
                    style={{
                        width: '48px',
                        height: '44px',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: isSendDisabled || isSending ? 'not-allowed' : 'pointer',
                        background: palette.gradient,
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '13px',
                        opacity: isSendDisabled || isSending ? 0.45 : 1,
                        boxShadow: '0 12px 24px rgba(99,102,241,0.35)',
                        transition: 'transform 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    aria-label="Send message"
                >
                    <SendIcon style={{ width: '20px', height: '20px' }} />
                </button>
            </div>
        </form>
    )
}
